import { defineStore } from 'pinia'
import { computed, ref, Ref, watchEffect } from 'vue'
import { useUserStore } from './user'
import { useSparePartsStore } from './spareParts'
import { useSessionStore } from './session'

import { DEBUG, EquipmentApi, NumDictionary } from '@/util'
import { AxiosResponse } from 'axios'
import { IBomDeviationDTO, IBomHeaderDTO, IBomItemQtyDTO } from '@/types/bom'
import { IEquipmentMasterDataDTO, IVarConfItemDTO } from '@/types/equipment'
import { IDocumentDTO } from '@/types/document'

/** store of currently active equipment, and it's related data */
export const useEquipmentStore = defineStore('equipmentStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _language: Ref<string> = ref('')
    const _currentSerialNo: Ref<string | null> = ref(null)
    const _latestResponse: Ref<AxiosResponse<IEquipmentMasterDataDTO> | null> = ref(null)
    const _bomData: Ref<IBomHeaderDTO[] | null> = ref(null)
    const _bomItemQtyDict: Ref<NumDictionary<IBomItemQtyDTO> | null> = ref(null)
    const _masterData: Ref<IEquipmentMasterDataDTO | null> = ref(null)
    const _documents: Ref<IDocumentDTO[] | null> = ref(null)
    const _varConfItems: Ref<IVarConfItemDTO[] | null> = ref(null)
    const _activeSerialNumber: Ref<string | null> = ref(null)
    const _bomDeviations: Ref<IBomDeviationDTO[] | null> = ref(null)

    const state = {
        _language,
        _currentSerialNo,
        _latestResponse,
        _bomData,
        _bomItemQtyDict,
        _masterData,
        _documents,
        _varConfItems,
        _activeSerialNumber,
        _bomDeviations
    }

    /********************************************************************************************************************
     GETTERS
     ********************************************************************************************************************/
    /** currently active language */
    const language = computed(() => _language.value)

    const masterData = computed(() => _masterData.value)
    const bomData = computed(() => _bomData.value)

    /** Dictionary of all material numbers within the equipment BOM, values = bom quantities */
    const bomItemQuantityDict = computed(() => _bomItemQtyDict.value)

    const documents = computed(() => _documents.value)
    const varConfItems = computed(() => _varConfItems.value)
    const activeSerialNo = computed(() => _activeSerialNumber)
    const bomDeviations = computed(() => _bomDeviations.value)

    const isOldEquipment = computed(() => hasOldSerialNo(masterData.value?.serialNo))

    /** Hide the documents tab, if no documents are present on an "old" device */
    const showDocumentTab = computed(() => !isOldEquipment.value || (documents.value?.length ?? 0) > 0)

    const getters = {
        language,
        masterData,
        bomData,
        bomItemQuantityDict,
        documents,
        varConfItems,
        activeSerialNo,
        bomDeviations,
        showDocumentTab
    }

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Sets the currently active equipment */
    function setDevice(serialNo: string): Promise<AxiosResponse<IEquipmentMasterDataDTO>> {
        if (serialNo != _currentSerialNo.value || _latestResponse.value == null) {
            DEBUG(`Loading data for equipment ${serialNo}...`)
            reset()
            const api = new EquipmentApi()

            return api.getMasterData(serialNo)
                .then(response => {
                    _latestResponse.value = response
                    _currentSerialNo.value = serialNo
                    _masterData.value = response.data
                    return response
                })
        }
        else {
            return new Promise(() => _latestResponse.value)
        }
    }

    function setActiveSerialNo(sn: string | null) {
        _activeSerialNumber.value = sn
    }

    function reset() {
        _currentSerialNo.value = null
        _masterData.value = null
        _bomData.value = null
        _documents.value = null
        _varConfItems.value = null
    //    _activeSerialNumber.value = null // BUGFIX: This would reset the active S/N during setDevice, after it has been set by the route guard
    }

    const actions = { setDevice, setActiveSerialNo, reset }

    /********************************************************************************************************************
     PRIVATE
     ********************************************************************************************************************/
    function hasOldSerialNo(serialNo?: string): boolean | null {
        if (serialNo == null) return null

        const yearString = serialNo.substring(2, 4)
        const year = parseInt(yearString)

        return !isNaN(year) && year < 24
    }

    function readBomData(equipmentNo: number | undefined) {
        if (!equipmentNo || userStore.loginType != 'user') {
            _bomData.value = null
            return
        }

        DEBUG(`Loading material numbers for equipment ${_currentSerialNo.value}...`)
        const api = new EquipmentApi()
        api.getSparePartNumbers(equipmentNo)
            .then(response => {
                _bomItemQtyDict.value = response.data
            })
            .catch(() => {
                _bomItemQtyDict.value = null
            })
    }

    function readDocuments(equipmentNo: number | undefined) {
        if (!equipmentNo) {
            _documents.value = null
            return
        }

        DEBUG(`Loading Documents for equipment ${_currentSerialNo.value}...`)
        const api = new EquipmentApi()
        api.getDocuments(equipmentNo)
            .then(response => _documents.value = response.data)
            .catch(() => _documents.value = [])
    }

    function readVariantConf(equipmentNo: number | undefined, language: string | undefined) {
        if (!equipmentNo || !language || userStore.loginType != 'user') {
            _varConfItems.value = null
            return
        }

        DEBUG(`Loading Configuration for equipment ${_currentSerialNo.value}...`)
        const api = new EquipmentApi()
        api.getVarConfItems(equipmentNo, language)
            .then(response => _varConfItems.value = response.data)
            .catch(() => _varConfItems.value = [])
    }

    function setLanguage(language: string) {
        if (language != _language.value) {
            _language.value = language
        }
    }

    // Extract BOM data for the specified spare parts from the spare part store
    function extractBomData(bomItemQtyDict: NumDictionary<IBomItemQtyDTO> | null, sparePartMap: Map<string, IBomHeaderDTO> | undefined) {
        if (bomItemQtyDict == null || sparePartMap == null || userStore.loginType != 'user') {
            DEBUG('equipmentStore: Setting BOM data to null')
            _bomData.value = null
            return
        }

        const result: IBomHeaderDTO[] = []
        const materialNumbers = new Set(Object.keys(bomItemQtyDict).map(Number))

        materialNumbers.forEach(materialNo => {
            const bom = sparePartMap.get(materialNo.toString())

            if (bom != null) {
                result.push(bom)
            }
        })

        DEBUG('equipmentStore: Setting BOM data to:', result)
        _bomData.value = result
    }

    /**
     * Read BOM deviations (between equipment and PO BOM) for the active equipment.
     * NOTE: Only during an admin login.
     */
    function readBomDeviations(equipmentNo: number | undefined) {
        if (!equipmentNo || !userStore.isAdmin) {
            _bomDeviations.value = null
            return
        }

        const api = new EquipmentApi()
        api.getBomDeviations(equipmentNo)
            .then(response => {
                _bomDeviations.value = response.data; DEBUG('###########',response.data)
            })
            .catch(() => _bomDeviations.value = [])
    }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    const userStore = useUserStore()

    userStore.$subscribe((mutation, state) => {
        setLanguage(state._language)
    })

    // Setup up as watches
    const sparePartStore = useSparePartsStore()
    watchEffect(() => readBomData(_masterData.value?.equipmentNo))
    watchEffect(() => extractBomData(_bomItemQtyDict.value, sparePartStore.sparePartMap))
    watchEffect(() => readDocuments(_masterData.value?.equipmentNo))
    watchEffect(() => readVariantConf(_masterData.value?.equipmentNo, _language.value))
    watchEffect(() => readBomDeviations(_masterData.value?.equipmentNo))

    if (!language.value) {
        setLanguage(userStore.language)
    }

    // Below is needed to ensure that equipment data is reloaded after a page refresh with F5
    const sessionStore = useSessionStore()
    const deviceSerialNo = sessionStore.getDeviceSerialNo()
    if (userStore.loginType == 'device' && deviceSerialNo != null && _currentSerialNo.value == null) {
        setDevice(deviceSerialNo)
    }

    return { ...state, ...getters, ...actions }
})